/*
 * STICKA - background removal in the customer's browser  (u2netp on onnxruntime-web; port of rembg.remove)
 *
 *   photo (RGBA img, already capped to MAX_SIDE)
 *     v  RGB -> 320x320 Lanczos, / max, ImageNet mean/std, CHW float32     (rembg sessions/base.py normalize)
 *     v  u2netp (4.6 MB, Apache-2.0, md5 8e83ca70e441ab06c318d82300c84806 - the same file rembg downloads)
 *     v  first output, min-max -> 0..255 -> back to the photo size (Lanczos) (rembg sessions/u2netp.py predict)
 *     v  post_process: opening (cross 3x3), gaussian sigma 2, threshold 127 (rembg bg.py, post_process_mask=True)
 *     v  naive cutout: pixels inside the mask keep their colour, the rest go transparent
 *
 * The cut-out is what the customer approves; the Mac makes the print file from THIS cut-out (engine/sticker.py,
 * transparent input = used as is), so the u2netp / u2net difference never reaches print.
 * Needs: sticka-custom-sticker.js (StickaSticker.resize) and the `ort` global (onnxruntime-web) or opts.ort.
 */
(function (root) {
  "use strict";

  var MAX_SIDE = 1600;          // the print needs <= ~950 px (5x8 cm at 300 dpi); phones choke on 12 MP arrays
  var MEAN = [0.485, 0.456, 0.406], STD = [0.229, 0.224, 0.225], SIZE = 320;
  var S = root.StickaSticker || (typeof require !== "undefined" ? require("./sticka-custom-sticker.js") : null);
  var _sessions = {};

  function session(model, ort) {            // model = URL, or the .onnx bytes (Uint8Array, node tests)
    var key = typeof model === "string" ? model : "bytes";
    if (!_sessions[key])
      _sessions[key] = ort.InferenceSession.create(model, { executionProviders: ["wasm"], graphOptimizationLevel: "all" })
        .catch(function (e) { delete _sessions[key]; throw e; });
    return _sessions[key];
  }

  function tensorOf(im) {                   // normalize()
    var small = S.resize(opaque(im), SIZE, SIZE), d = small.data, n = SIZE * SIZE, mx = 0, i, c;
    for (i = 0; i < n; i++) for (c = 0; c < 3; c++) if (d[i * 4 + c] > mx) mx = d[i * 4 + c];
    mx = Math.max(mx, 1e-6);
    var t = new Float32Array(3 * n);
    for (c = 0; c < 3; c++) for (i = 0; i < n; i++) t[c * n + i] = (d[i * 4 + c] / mx - MEAN[c]) / STD[c];
    return t;
  }

  function opaque(im) {                     // .convert("RGB"): colour as stored, alpha ignored
    var o = { w: im.w, h: im.h, data: new Uint8ClampedArray(im.data) };
    for (var i = 3; i < o.data.length; i += 4) o.data[i] = 255;
    return o;
  }

  // scipy.ndimage.gaussian_filter(sigma=2), truncate 4, mode "reflect" (edge sample repeated)
  function gaussReflect(src, w, h, s) {
    var r = Math.floor(4 * s + 0.5), k = [], sum = 0, i, x, y, j, a;
    for (i = -r; i <= r; i++) { k.push(Math.exp(-0.5 * i * i / (s * s))); sum += k[k.length - 1]; }
    for (i = 0; i < k.length; i++) k[i] /= sum;
    function rf(p, n) { while (p < 0 || p >= n) p = p < 0 ? -p - 1 : 2 * n - p - 1; return p; }
    var tmp = new Float32Array(w * h), out = new Float32Array(w * h);
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      a = 0; for (j = -r; j <= r; j++) a += src[y * w + rf(x + j, w)] * k[j + r]; tmp[y * w + x] = a;
    }
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      a = 0; for (j = -r; j <= r; j++) a += tmp[rf(y + j, h) * w + x] * k[j + r]; out[y * w + x] = a;
    }
    return out;
  }

  function postProcess(m, w, h) {           // opening(mask, disk(1)) -> gaussian -> threshold
    function morph(src, mn) {
      var o = new Uint8Array(w * h), x, y, i, v, q;
      for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
        i = y * w + x; v = src[i];
        q = x > 0 ? src[i - 1] : (mn ? 255 : 0); v = mn ? Math.min(v, q) : Math.max(v, q);
        q = x < w - 1 ? src[i + 1] : (mn ? 255 : 0); v = mn ? Math.min(v, q) : Math.max(v, q);
        q = y > 0 ? src[i - w] : (mn ? 255 : 0); v = mn ? Math.min(v, q) : Math.max(v, q);
        q = y < h - 1 ? src[i + w] : (mn ? 255 : 0); v = mn ? Math.min(v, q) : Math.max(v, q);
        o[i] = v;
      }
      return o;
    }
    var op = morph(morph(m, true), false), f = new Float32Array(w * h), i;
    for (i = 0; i < f.length; i++) f[i] = op[i];
    f = gaussReflect(f, w, h, 2);
    var out = new Uint8Array(w * h);
    for (i = 0; i < f.length; i++) out[i] = f[i] < 127 ? 0 : 255;
    return out;
  }

  /* cap(im) -> the image scaled down so its long side <= MAX_SIDE (Lanczos); smaller images unchanged */
  function cap(im, side) {
    side = side || MAX_SIDE;
    var k = side / Math.max(im.w, im.h);
    return k >= 1 ? im : S.resize(im, Math.max(1, Math.round(im.w * k)), Math.max(1, Math.round(im.h * k)));
  }

  /* removeBackground(im, {modelUrl | model (bytes), ort}) -> Promise {cutout: img, mask: Uint8Array, ms: {prep, model, post}} */
  function removeBackground(im, opts) {
    var ort = (opts && opts.ort) || root.ort, t0 = Date.now(), times = {};
    var input = tensorOf(im);
    times.prep = Date.now() - t0;
    return session(opts.model || opts.modelUrl, ort).then(function (sess) {
      var t1 = Date.now(), feeds = {};
      feeds[sess.inputNames[0]] = new ort.Tensor("float32", input, [1, 3, SIZE, SIZE]);
      return sess.run(feeds).then(function (out) {
        times.model = Date.now() - t1;
        var t2 = Date.now(), p = out[sess.outputNames[0]].data, n = SIZE * SIZE, mn = Infinity, mx = -Infinity, i;
        for (i = 0; i < n; i++) { if (p[i] < mn) mn = p[i]; if (p[i] > mx) mx = p[i]; }
        var m = { w: SIZE, h: SIZE, data: new Uint8ClampedArray(n * 4) };
        for (i = 0; i < n; i++) {
          var v = Math.floor((p[i] - mn) / (mx - mn) * 255);
          m.data[i * 4] = m.data[i * 4 + 1] = m.data[i * 4 + 2] = v; m.data[i * 4 + 3] = 255;
        }
        var big = S.resize(m, im.w, im.h), N = im.w * im.h, mask = new Uint8Array(N);
        for (i = 0; i < N; i++) mask[i] = big.data[i * 4];
        mask = postProcess(mask, im.w, im.h);
        var cut = { w: im.w, h: im.h, data: new Uint8ClampedArray(N * 4) };
        for (i = 0; i < N; i++) if (mask[i]) {            // Image.composite(img, empty, mask), binary mask
          cut.data[i * 4] = im.data[i * 4]; cut.data[i * 4 + 1] = im.data[i * 4 + 1];
          cut.data[i * 4 + 2] = im.data[i * 4 + 2]; cut.data[i * 4 + 3] = 255;
        }
        times.post = Date.now() - t2;
        return { cutout: cut, mask: mask, ms: times };
      });
    });
  }

  var api = { removeBackground: removeBackground, cap: cap, MAX_SIDE: MAX_SIDE };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.StickaBg = api;
})(this);
